// src/components/ui/FormFields/MagicTextField.jsx
import React, { useState } from 'react';
import { 
  TextField, 
  InputAdornment, 
  Box,
  alpha,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';

// Create animations
const focusShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// No need to define our own glow effect - the theme already has a global one defined in CssBaseline
// The animation 'glow' is defined in components.js and uses the secondary color by default

// Subtle float animation
const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
`;

// Shake animation for error state
const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-2px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(2px);
  }
`;

// Success microinteraction animation
const success = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
  }
`;

// Focus breathe effect - used in theme config, not directly
// Kept here as reference for design system docs
/*
const breathe = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 2px rgba(var(--focus-color), 0.2);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(var(--focus-color), 0.3);
  }
`;
*/

// Styled TextField wrapper
const StyledTextFieldWrapper = styled(Box)(({ theme }) => {
  return {
    position: 'relative',
    width: '100%',
    marginBottom: theme.spacing(2),
  };
});

// Styled TextField with enhanced effects
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'magical' && prop !== 'floating',
})(({ theme, magical, color = 'primary', error, focused, floating, helperText }) => {
  const colors = theme.palette;
  const tokens = theme.aghrba?.tokens;
  
  // Use theme tokens for border radius
  const borderRadius = tokens?.borderRadius?.input || 30;
  
  // Use theme tokens for height
  const inputHeight = tokens?.buttonHeight?.small || 48.5;
  
  // Use theme transitions
  const inputTransition = tokens?.transitions?.standard || 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';

  return {
    width: '100%',
    
    // Apply floating animation if enabled (using theme tokens)
    ...(floating && {
      animation: tokens?.animations?.float || `${float} 4s ease-in-out infinite`,
    }),
    
    // Base styles for the input field
    '& .MuiOutlinedInput-root': {
      minHeight: inputHeight,
      borderRadius,
      transition: 'all 0.3s ease',
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(colors.background.paper, 0.5)
        : alpha(colors.background.paper, 0.95),

      display: 'flex',
      
      // Improved notched outline for better label visibility
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.mode === 'dark' 
          ? alpha(colors.divider, 0.4)
          : alpha(colors.divider, 0.5),
        borderWidth: '1px',
        transition: 'border-color 0.3s ease',
        // Ensure the notch for the label is properly sized
        legend: {
          // Increase width to prevent label truncation
          '& > span': {
            opacity: 1,
            padding: '0 4px',
            maxWidth: '100%',
            width: 'auto',
            display: 'block',
            visibility: 'visible',
          }
        }
      },
      
      // Standard hover state
      '&:hover:not(.Mui-focused):not(.Mui-error)': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main,
          borderWidth: '1px',
        },
      },
      
      // Magical focused state - strict box-shadow: none to ensure no shadow appears
      ...(magical && {
        // IMPORTANT: Force no box shadow in ALL states
        boxShadow: 'none !important',

        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: (focused && !error) ? 
            (theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main) :
            (theme.palette.mode === 'dark' ? alpha(colors.divider, 0.4) : alpha(colors.divider, 0.5)),
          borderWidth: '1px',
          borderRadius: 'inherit',
          transition: 'border-color 0.3s ease',
        },
      }),
      
      // Standard focused state
      ...(!magical && focused && !error && {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main,
          borderWidth: '2px',
          transition: 'border-color 0.3s ease',
        },
      }),
      
      // Error state - standard Material-UI error styling with higher priority
      '&.Mui-error': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: `${colors.error.main} !important`,
          borderWidth: '2px',
        },
        // Ensure error styles override focus styles
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: `${colors.error.main} !important`,
          borderWidth: '2px',
        },
      },
      
      // Refined input styling
      '& .MuiInputBase-input': {
        padding: '14px 16px',
        transition: 'all 0.3s ease',
        height: 'auto',
        fontSize: '0.875rem',
        color: colors.text.primary,
      },
      
      // Better aligned adornments with properly sized icons
      '& .MuiInputAdornment-root': {
        minHeight: 0,
        height: 'auto',
        marginTop: 0,
        marginBottom: 0,
        
        '& svg': {
          color: theme.palette.text.secondary,
          fontSize: '1.25rem', // Consistent icon size with search
        },
        
        '&.MuiInputAdornment-positionStart': {
          marginRight: '8px',
        },
        
        '&.MuiInputAdornment-positionEnd': {
          marginLeft: '8px',
        },
        
        '& .MuiIconButton-root': {
          padding: '4px',
        },
      },
    },
    
    // Enhanced label styles - fixing visibility issues
    '& .MuiInputLabel-root': {
      transition: 'color 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      color: alpha(colors.text.primary, theme.palette.mode === 'dark' ? 0.9 : 0.87),
      fontWeight: 500,
      zIndex: 1,
      opacity: 1, // Ensure labels are visible
      
      // Ensure transform is properly set for label positioning
      ...(magical && {
        transform: 'translate(14px, -9px) scale(0.75)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)',
          opacity: 1,
          visibility: 'visible',
        },
      }),
      
      // Make label visible with proper styling in all states
      '&.MuiFormLabel-filled, &.MuiInputLabel-shrink': {
        opacity: 1,
        visibility: 'visible',
      },
      
      // Remove all normal/error styling overrides and consolidate into a single style block
      // This ensures only one label appears with correct styling
      '&.Mui-focused:not(.Mui-error)': {
        opacity: 1,
        visibility: 'visible',
        color: alpha(theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main, theme.palette.mode === 'dark' ? 1 : 0.9),
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
      '&.Mui-error': {
        opacity: 1,
        visibility: 'visible',
        color: `${alpha(colors.error.main, theme.palette.mode === 'dark' ? 1 : 0.9)} !important`,
        fontWeight: 600,
        letterSpacing: '0',
      },
    },
    
    // Ensure helper text never shows twice
    '& .MuiFormHelperText-root + .MuiFormHelperText-root': {
      display: 'none',
    },
    
    // Helper text styling
    '& .MuiFormHelperText-root': {
      marginLeft: theme.spacing(1.5),
      marginTop: 4,
      fontSize: '0.75rem',
      transition: 'color 0.3s ease',
      color: colors.text.secondary,
      
      // Error styling
      '&.Mui-error': {
        color: colors.error.main,
        marginTop: 4,
      },
    },
  };
});

/**
 * Enhanced text field with magical effects that match the theme
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Input label
 * @param {string|node} props.helperText - Helper text displayed below the input
 * @param {boolean} props.error - Whether the input has an error
 * @param {node} props.startAdornment - Start adornment (icon, etc.)
 * @param {node} props.endAdornment - End adornment (icon, etc.)
 * @param {boolean} props.magical - Whether to apply magical effects (glowing, animations)
 * @param {boolean} props.floating - Whether the field should have a floating animation
 * @param {string} props.color - Color theme ('primary', 'secondary', 'accent')
 * @param {string} props.variant - Input variant ('outlined', 'filled', 'standard')
 * @param {boolean} props.fullWidth - Whether the input takes up full width
 * @param {Object} props.InputProps - Additional props for InputComponent
 * @param {ref} ref - Forwarded ref
 */
const MagicTextField = React.forwardRef(({
  label,
  helperText,
  error,
  startAdornment,
  endAdornment,
  magical = true, // Default to true for backward compatibility
  floating = false,
  color = 'primary',
  variant = 'outlined',
  fullWidth = true,
  size = 'medium',
  InputProps = {},
  placeholder, // Add explicit placeholder prop
  fieldStrength, // Add field strength indicator for password/security fields
  completed, // Add completed status for form progression
  showCompletionStatus = false, // Control whether to show completion status
  ...props
}, ref) => {
  // Remove magical, floating, and other custom props to avoid passing them to DOM elements
  const { magical: _, floating: __, ...textFieldProps } = props;
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Handle focus events
  const handleFocus = (e) => {
    setFocused(true);
    if (props.onFocus) props.onFocus(e);
  };

  const handleBlur = (e) => {
    setFocused(false);
    setHasInteracted(true);
    if (props.onBlur) props.onBlur(e);
  };

  // Determine field status based on multiple factors
  const isCompleted = Boolean(
    completed ||
    (props.value && hasInteracted && !error && showCompletionStatus)
  );

  // Show success animation briefly on completion
  React.useEffect(() => {
    if (isCompleted) {
      // Would add state for success animation
    }
  }, [isCompleted]);

  // Determine appropriate adornment colors based on state
  const adornmentColor = error
    ? 'error'
    : isCompleted
      ? 'success'
      : focused
        ? color
        : theme.palette.mode === 'dark' ? 'action' : 'primary';

  // Create status indicator for end adornment
  const statusIndicator = isCompleted ? (
    <Box component="span" sx={{
      color: theme.palette.success.main,
      svg: { fontSize: '1.2rem' }
    }}>
      ✓
    </Box>
  ) : null;

  // Process the adornment icons to update their color
  const processedStartAdornment = startAdornment
    ? React.isValidElement(startAdornment) && startAdornment.type?.muiName === 'SvgIcon'
      ? React.cloneElement(startAdornment, {
          color: adornmentColor,
          sx: {
            opacity: 1,
            transition: 'opacity 0.3s ease, color 0.3s ease',
            transform: focused ? 'scale(1.1)' : 'scale(1)',
            animation: isCompleted ? `${success} 0.4s ease-out` : 'none',
            color: error ? theme.palette.error.main :
                   isCompleted ? theme.palette.success.main :
                   theme.palette.mode === 'dark' ? undefined : theme.palette.primary.main,
            fontSize: '1.2rem',
            ...startAdornment.props?.sx
          }
        })
      : startAdornment
    : null;

  // Process end adornment with status indicator
  const processedEndAdornment = (endAdornment || statusIndicator)
    ? <>
        {statusIndicator}
        {React.isValidElement(endAdornment) && endAdornment.type?.muiName === 'SvgIcon'
          ? React.cloneElement(endAdornment, {
              color: adornmentColor,
              sx: {
                opacity: 1,
                transition: 'opacity 0.3s ease, color 0.3s ease',
                transform: focused ? 'scale(1.1)' : 'scale(1)',
                animation: isCompleted ? `${success} 0.4s ease-out` : 'none',
                color: error ? theme.palette.error.main :
                       isCompleted ? theme.palette.success.main :
                       theme.palette.mode === 'dark' ? undefined : theme.palette.primary.main,
                fontSize: '1.2rem',
                ...endAdornment.props?.sx
              }
            })
          : endAdornment}
      </>
    : null;

  // Add field strength indicator if requested (for passwords, etc.)
  const fieldStrengthIndicator = fieldStrength && !error ? (
    <Box sx={{
      mt: 0.5,
      height: 3,
      width: '100%',
      backgroundColor: alpha(theme.palette.text.disabled, 0.2),
      borderRadius: 3,
      overflow: 'hidden'
    }}>
      <Box sx={{
        height: '100%',
        width: `${Math.min(100, fieldStrength * 25)}%`,
        backgroundColor: fieldStrength <= 1 ? theme.palette.error.main :
                        fieldStrength <= 2 ? theme.palette.warning.main :
                        fieldStrength <= 3 ? theme.palette.info.main :
                        theme.palette.success.main,
        transition: 'width 0.3s ease, background-color 0.3s ease'
      }} />
    </Box>
  ) : null;
  
  // When magical is true, make sure the label appears shrunk by default
  // This creates the effect of the label always being at the top
  const inputLabelProps = magical ? {
    // For backward compatibility, always show label at the top
    shrink: true
  } : {};
  
  return (
    <StyledTextFieldWrapper>
      <StyledTextField
        ref={ref}
        label={label}
        error={Boolean(error)}
        variant={variant}
        fullWidth={fullWidth}
        color={color}
        magical={magical}
        floating={floating}
        focused={magical || focused}
        size={size}
        helperText={helperText}
        placeholder={placeholder} // Explicitly pass placeholder
        InputLabelProps={{
          ...inputLabelProps,
          shrink: magical ? true : (placeholder ? true : undefined) // Force label to be shrunk when magical is true or placeholder exists
        }}
        InputProps={{
          ...InputProps,
          startAdornment: processedStartAdornment && (
            <InputAdornment position="start">
              {processedStartAdornment}
            </InputAdornment>
          ),
          endAdornment: processedEndAdornment && (
            <InputAdornment position="end">
              {processedEndAdornment}
            </InputAdornment>
          ),
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...textFieldProps}
      />
      {fieldStrengthIndicator}
    </StyledTextFieldWrapper>
  );
});

MagicTextField.displayName = 'MagicTextField';

export default MagicTextField;